import { getCachedSession } from '../hooks/useAuth';
/**
 * SRE V2 - Surface Replacement Engine Premium
 * ============================================
 * SlabReality - Production-Grade Surface Replacement
 * 
 * Features:
 * - Premium before/after slider
 * - Quality metrics display (Realism, Lighting, Edge, Texture, Continuity)
 * - Accept/Reject/Alternative workflow
 * - Save to project functionality
 * - Render history persistence
 * 
 * @author SlabReality Team
 * @version 2.0
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, Upload, Image as ImageIcon, Play, RefreshCw, 
  Download, Check, X, Loader2, Sparkles, ChevronLeft, ChevronRight,
  Sun, Layers, Target, Palette, Diamond, Zap, Heart, Save,
  LayoutGrid, AlertCircle, CheckCircle, History
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

// =============================================================================
// BEFORE/AFTER SLIDER COMPONENT
// =============================================================================
const BeforeAfterSlider = ({ beforeImage, afterImage, className = '' }) => {
  const containerRef = useRef(null);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  
  const handleMove = useCallback((clientX) => {
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  }, []);
  
  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    handleMove(e.clientX);
  }, [isDragging, handleMove]);
  
  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    handleMove(e.touches[0].clientX);
  }, [isDragging, handleMove]);
  
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', () => setIsDragging(false));
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', () => setIsDragging(false));
    }
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', () => setIsDragging(false));
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', () => setIsDragging(false));
    };
  }, [isDragging, handleMouseMove, handleTouchMove]);
  
  return (
    <div 
      ref={containerRef}
      className={`relative overflow-hidden rounded-2xl cursor-ew-resize select-none ${className}`}
      onMouseDown={(e) => { setIsDragging(true); handleMove(e.clientX); }}
      onTouchStart={(e) => { setIsDragging(true); handleMove(e.touches[0].clientX); }}
    >
      {/* Before Image (Full) */}
      <div className="relative w-full aspect-[16/10]">
        <img 
          src={beforeImage}
          alt="Before"
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
        />
        
        {/* After Image (Clipped) */}
        <div 
          className="absolute inset-0 overflow-hidden"
          style={{ clipPath: `inset(0 0 0 ${sliderPosition}%)` }}
        >
          <img 
            src={afterImage}
            alt="After"
            className="absolute inset-0 w-full h-full object-cover"
            draggable={false}
          />
        </div>
        
        {/* Slider Line */}
        <div 
          className="absolute top-0 bottom-0 w-1 bg-white shadow-lg"
          style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
        >
          {/* Slider Handle */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-xl flex items-center justify-center">
            <div className="flex items-center gap-0.5">
              <ChevronLeft className="w-4 h-4 text-gray-600" />
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </div>
          </div>
        </div>
        
        {/* Labels */}
        <div className="absolute bottom-4 left-4 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-full text-white text-sm font-medium">
          Before
        </div>
        <div className="absolute bottom-4 right-4 px-3 py-1.5 bg-[#C8A36A]/90 backdrop-blur-sm rounded-full text-black text-sm font-medium">
          After
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// QUALITY METRIC CARD
// =============================================================================
const QualityMetric = ({ label, score, icon: Icon }) => {
  const getScoreColor = (s) => {
    if (s >= 80) return 'text-green-400';
    if (s >= 60) return 'text-[#C8A36A]';
    if (s >= 40) return 'text-yellow-400';
    return 'text-red-400';
  };
  
  const getBarColor = (s) => {
    if (s >= 80) return 'bg-green-400';
    if (s >= 60) return 'bg-[#C8A36A]';
    if (s >= 40) return 'bg-yellow-400';
    return 'bg-red-400';
  };
  
  return (
    <div className="bg-white/[0.03] rounded-xl p-4 border border-white/5">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-[#C8A36A]" />}
          <span className="text-xs uppercase tracking-wider text-[#F5F1E8]/60">{label}</span>
        </div>
        <span className={`text-lg font-medium ${getScoreColor(score)}`}>{Math.round(score)}%</span>
      </div>
      <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
        <div 
          className={`h-full rounded-full transition-all duration-500 ${getBarColor(score)}`}
          style={{ width: `${score}%` }}
        />
      </div>
    </div>
  );
};

// =============================================================================
// MATERIAL SELECTOR
// =============================================================================
const MaterialSelector = ({ materials, selected, onSelect, loading }) => {
  const [search, setSearch] = useState('');
  
  const filtered = materials.filter(m => 
    m.name?.toLowerCase().includes(search.toLowerCase()) ||
    m.stone_type?.toLowerCase().includes(search.toLowerCase())
  );
  
  return (
    <div className="space-y-4">
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search materials..."
        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-[#F5F1E8] placeholder:text-[#F5F1E8]/40 focus:outline-none focus:border-[#C8A36A]/50"
      />
      
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[400px] overflow-y-auto pr-2">
        {loading ? (
          <div className="col-span-full py-8 text-center text-[#F5F1E8]/40">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading materials...
          </div>
        ) : filtered.length === 0 ? (
          <div className="col-span-full py-8 text-center text-[#F5F1E8]/40">
            No materials found
          </div>
        ) : (
          filtered.map((mat) => (
            <div
              key={mat.id || mat.source_id}
              onClick={() => onSelect(mat)}
              className={`cursor-pointer rounded-xl overflow-hidden border-2 transition-all ${
                selected?.id === mat.id || selected?.source_id === mat.source_id
                  ? 'border-[#C8A36A]' 
                  : 'border-transparent hover:border-[#C8A36A]/50'
              }`}
            >
              <div className="aspect-square relative">
                <img 
                  src={mat.image_main || mat.image_url || '/placeholder-material.jpg'}
                  alt={mat.name}
                  className="w-full h-full object-cover"
                />
                {(selected?.id === mat.id || selected?.source_id === mat.source_id) && (
                  <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#C8A36A] flex items-center justify-center">
                    <Check className="w-4 h-4 text-black" />
                  </div>
                )}
              </div>
              <div className="p-2 bg-white/[0.03]">
                <div className="text-[10px] text-[#C8A36A] uppercase">{mat.stone_type || mat.category}</div>
                <div className="text-xs text-[#F5F1E8] truncate">{mat.name}</div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================
const SREV2Page = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get pre-selected material from URL
  const preSelectedMaterialId = searchParams.get('material');
  const preSelectedMaterialName = searchParams.get('name');
  
  // State
  const [step, setStep] = useState(1); // 1: Upload, 2: Select Material, 3: Result
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [segmentationMask, setSegmentationMask] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [loading, setLoading] = useState(false);
  const [materialsLoading, setMaterialsLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [qualityMetrics, setQualityMetrics] = useState(null);
  const [currentRenderHistoryId, setCurrentRenderHistoryId] = useState(null);
  
  // Load materials on mount
  useEffect(() => {
    fetchMaterials();
  }, []);
  
  // Pre-select material if provided in URL
  useEffect(() => {
    if (preSelectedMaterialId && materials.length > 0) {
      const mat = materials.find(m => 
        m.id === preSelectedMaterialId || 
        m.source_id === preSelectedMaterialId
      );
      if (mat) {
        setSelectedMaterial(mat);
      }
    }
  }, [preSelectedMaterialId, materials]);
  
  const fetchMaterials = async () => {
    setMaterialsLoading(true);
    try {
      const res = await axios.get(`${API}/api/materials`, { params: { limit: 50 } });
      const mats = res.data.materials || res.data || [];
      setMaterials(mats);
    } catch (err) {
      console.error('Failed to fetch materials:', err);
      toast.error('Failed to load materials');
    }
    setMaterialsLoading(false);
  };
  
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setLoading(true);
    
    // Create preview URL
    const url = URL.createObjectURL(file);
    setUploadedImage(file);
    setUploadedImageUrl(url);
    
    // Convert to base64 for API
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result.split(',')[1];
      
      // Run segmentation
      try {
        const res = await axios.post(`${API}/api/sre-v2/segment`, {
          image_base64: base64
        }, { timeout: 60000 });
        
        if (res.data.success && res.data.mask_url) {
          setSegmentationMask(res.data);
          toast.success('Surface detected!');
          setStep(2);
        } else {
          toast.error('No surface detected. Try another image.');
        }
      } catch (err) {
        console.error('Segmentation error:', err);
        toast.error('Failed to detect surface. Please try again.');
      }
      setLoading(false);
    };
    reader.readAsDataURL(file);
  };
  
  const handleReplace = async () => {
    if (!uploadedImageUrl || !selectedMaterial || !segmentationMask) {
      toast.error('Please select an image and material');
      return;
    }
    
    setProcessing(true);
    setStep(3);
    
    try {
      // Get corners from segmentation
      const corners = segmentationMask.corners || [
        [0, 0], 
        [segmentationMask.width || 800, 0],
        [segmentationMask.width || 800, segmentationMask.height || 600],
        [0, segmentationMask.height || 600]
      ];
      
      // Call replace endpoint
      const res = await axios.post(
        `${API}/api/sre-v2/slabs/${segmentationMask.slab_id || 'temp'}/replace-surface`,
        {
          material_id: selectedMaterial.id || selectedMaterial.source_id,
          corners: corners,
          preserve_lighting: true,
          preserve_texture: true
        },
        { timeout: 120000 }
      );
      
      if (res.data.success && res.data.result_url) {
        setResult({
          before: uploadedImageUrl,
          after: res.data.result_url.startsWith('http') 
            ? res.data.result_url 
            : `${API}${res.data.result_url}`,
          metrics: res.data.quality_metrics || res.data.metrics
        });
        
        // Extract quality metrics
        if (res.data.quality_metrics || res.data.metrics) {
          setQualityMetrics(res.data.quality_metrics || res.data.metrics);
        }
        
        // Save to render history
        await saveRenderHistory(res.data);
        
        toast.success('Surface replaced successfully!');
      } else {
        throw new Error(res.data.error || 'Replacement failed');
      }
    } catch (err) {
      console.error('Replace error:', err);
      toast.error('Failed to replace surface. Please try again.');
      setStep(2);
    }
    setProcessing(false);
  };
  
  const saveRenderHistory = async (resultData) => {
    try {
      const session = getCachedSession();
      
      const historyRecord = {
        user_id: session?.user?.id || null,
        source_image_url: uploadedImageUrl,
        material_id: selectedMaterial?.id || selectedMaterial?.source_id,
        material_name: selectedMaterial?.name,
        result_image_url: resultData.result_url,
        mask_image_url: segmentationMask?.mask_url,
        quality_metrics: resultData.quality_metrics || resultData.metrics || {},
        status: 'draft',
        created_at: new Date().toISOString()
      };
      
      const { data, error } = await supabase
        .from('sre_render_history')
        .insert(historyRecord)
        .select()
        .single();
      
      if (data) {
        setCurrentRenderHistoryId(data.id);
      }
    } catch (err) {
      console.error('Failed to save render history:', err);
    }
  };
  
  const handleAction = async (action) => {
    if (!currentRenderHistoryId) return;
    
    try {
      const statusMap = {
        approve: 'approved',
        reject: 'rejected',
        alternative: 'alternative_requested',
        save: 'saved_to_project'
      };
      
      await supabase
        .from('sre_render_history')
        .update({ status: statusMap[action], updated_at: new Date().toISOString() })
        .eq('id', currentRenderHistoryId);
      
      if (action === 'approve') {
        toast.success('Result approved and saved!');
      } else if (action === 'reject') {
        toast.info('Result rejected. Try another material?');
        setStep(2);
        setResult(null);
      } else if (action === 'alternative') {
        toast.info('Select an alternative material');
        setStep(2);
        setResult(null);
      } else if (action === 'save') {
        toast.success('Saved to your project!');
      }
    } catch (err) {
      console.error('Action error:', err);
      toast.error('Failed to update status');
    }
  };
  
  const handleDownload = () => {
    if (!result?.after) return;
    
    const link = document.createElement('a');
    link.href = result.after;
    link.download = `sre-result-${Date.now()}.jpg`;
    link.click();
  };
  
  const resetFlow = () => {
    setStep(1);
    setUploadedImage(null);
    setUploadedImageUrl(null);
    setSegmentationMask(null);
    setResult(null);
    setQualityMetrics(null);
    setCurrentRenderHistoryId(null);
  };
  
  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-[#C8A36A] to-[#D4AF37] rounded-lg flex items-center justify-center">
              <Diamond className="w-4 h-4 text-black" />
            </div>
            <span className="text-[#F5F1E8] font-semibold tracking-tight">SlabReality</span>
          </Link>
          
          <h1 className="text-lg font-serif text-[#F5F1E8]">
            Surface Replacement <span className="text-[#C8A36A]">Engine V2</span>
          </h1>
          
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[#F5F1E8]/60 hover:text-[#C8A36A] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back</span>
          </button>
        </div>
      </header>
      
      {/* Progress Steps */}
      <div className="max-w-[1400px] mx-auto px-6 py-6">
        <div className="flex items-center justify-center gap-4">
          {[
            { num: 1, label: 'Upload Image' },
            { num: 2, label: 'Select Material' },
            { num: 3, label: 'Review Result' }
          ].map((s, i) => (
            <React.Fragment key={s.num}>
              <div className={`flex items-center gap-2 ${step >= s.num ? 'text-[#C8A36A]' : 'text-[#F5F1E8]/30'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= s.num ? 'bg-[#C8A36A] text-black' : 'bg-white/5 text-[#F5F1E8]/30'
                }`}>
                  {step > s.num ? <Check className="w-4 h-4" /> : s.num}
                </div>
                <span className="text-sm hidden sm:block">{s.label}</span>
              </div>
              {i < 2 && <div className={`w-16 h-px ${step > s.num ? 'bg-[#C8A36A]' : 'bg-white/10'}`} />}
            </React.Fragment>
          ))}
        </div>
      </div>
      
      {/* Main Content */}
      <main className="max-w-[1400px] mx-auto px-6 pb-16">
        {/* Step 1: Upload */}
        {step === 1 && (
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-serif text-[#F5F1E8] mb-3">Upload Your Space</h2>
              <p className="text-[#F5F1E8]/60">Upload a photo of a kitchen, bathroom, or any surface you want to transform</p>
            </div>
            
            <label className="block">
              <div className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all ${
                loading 
                  ? 'border-[#C8A36A]/50 bg-[#C8A36A]/5' 
                  : 'border-[#C8A36A]/30 hover:border-[#C8A36A] hover:bg-white/[0.02]'
              }`}>
                {loading ? (
                  <div className="space-y-4">
                    <Loader2 className="w-12 h-12 text-[#C8A36A] mx-auto animate-spin" />
                    <p className="text-[#F5F1E8]/60">Detecting surface...</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Upload className="w-12 h-12 text-[#C8A36A] mx-auto" />
                    <div>
                      <p className="text-[#F5F1E8] font-medium mb-1">Drop an image here or click to browse</p>
                      <p className="text-[#F5F1E8]/40 text-sm">JPG, PNG up to 10MB</p>
                    </div>
                  </div>
                )}
              </div>
              <input 
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                disabled={loading}
              />
            </label>
          </div>
        )}
        
        {/* Step 2: Select Material */}
        {step === 2 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Preview */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-[#F5F1E8]">Your Image</h3>
              <div className="relative rounded-2xl overflow-hidden bg-[#0A0A0A]">
                <img 
                  src={uploadedImageUrl}
                  alt="Uploaded"
                  className="w-full aspect-video object-cover"
                />
                {segmentationMask?.mask_url && (
                  <div className="absolute inset-0 bg-[#C8A36A]/20 mix-blend-overlay" />
                )}
                <div className="absolute bottom-4 left-4 px-3 py-1.5 bg-green-500/80 rounded-full text-white text-sm flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Surface detected
                </div>
              </div>
              
              {/* Pre-selected material info */}
              {preSelectedMaterialName && (
                <div className="bg-[#C8A36A]/10 border border-[#C8A36A]/20 rounded-xl p-4">
                  <div className="text-xs uppercase text-[#C8A36A]/70">Pre-selected Material</div>
                  <div className="text-lg text-[#F5F1E8]">{preSelectedMaterialName}</div>
                </div>
              )}
            </div>
            
            {/* Material Selection */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-[#F5F1E8]">Choose Material</h3>
              <MaterialSelector
                materials={materials}
                selected={selectedMaterial}
                onSelect={setSelectedMaterial}
                loading={materialsLoading}
              />
              
              {/* Replace Button */}
              <button
                onClick={handleReplace}
                disabled={!selectedMaterial || processing}
                className={`w-full py-4 rounded-xl font-medium flex items-center justify-center gap-2 transition-all ${
                  selectedMaterial && !processing
                    ? 'bg-gradient-to-r from-[#C8A36A] to-[#D4AF37] text-black hover:shadow-lg hover:shadow-[#C8A36A]/20'
                    : 'bg-white/10 text-[#F5F1E8]/40 cursor-not-allowed'
                }`}
              >
                {processing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Play className="w-5 h-5" />
                    Replace Surface
                  </>
                )}
              </button>
            </div>
          </div>
        )}
        
        {/* Step 3: Result */}
        {step === 3 && (
          <div className="space-y-8">
            {processing ? (
              <div className="max-w-2xl mx-auto text-center py-16">
                <div className="w-20 h-20 rounded-full bg-[#C8A36A]/10 flex items-center justify-center mx-auto mb-6">
                  <Loader2 className="w-10 h-10 text-[#C8A36A] animate-spin" />
                </div>
                <h2 className="text-2xl font-serif text-[#F5F1E8] mb-3">Processing Replacement...</h2>
                <p className="text-[#F5F1E8]/60">Applying material with advanced lighting and edge blending</p>
              </div>
            ) : result ? (
              <>
                {/* Before/After Slider */}
                <BeforeAfterSlider
                  beforeImage={result.before}
                  afterImage={result.after}
                  className="max-w-4xl mx-auto"
                />
                
                {/* Quality Metrics */}
                {qualityMetrics && (
                  <div className="max-w-4xl mx-auto">
                    <h3 className="text-lg font-medium text-[#F5F1E8] mb-4 flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-[#C8A36A]" />
                      Quality Metrics
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <QualityMetric 
                        label="Realism" 
                        score={qualityMetrics.realism_score || 75} 
                        icon={Target}
                      />
                      <QualityMetric 
                        label="Lighting" 
                        score={qualityMetrics.lighting_score || 80} 
                        icon={Sun}
                      />
                      <QualityMetric 
                        label="Edge Quality" 
                        score={qualityMetrics.edge_quality_score || 85} 
                        icon={Layers}
                      />
                      <QualityMetric 
                        label="Texture" 
                        score={qualityMetrics.texture_preservation_score || 70} 
                        icon={LayoutGrid}
                      />
                      <QualityMetric 
                        label="Continuity" 
                        score={qualityMetrics.continuity_score || 90} 
                        icon={Palette}
                      />
                    </div>
                  </div>
                )}
                
                {/* Actions */}
                <div className="max-w-4xl mx-auto">
                  <div className="flex flex-wrap gap-4 justify-center">
                    <button
                      onClick={() => handleAction('approve')}
                      className="px-6 py-3 bg-green-600 text-white rounded-xl font-medium flex items-center gap-2 hover:bg-green-500 transition-colors"
                    >
                      <Check className="w-5 h-5" />
                      Approve
                    </button>
                    <button
                      onClick={() => handleAction('reject')}
                      className="px-6 py-3 bg-white/10 text-[#F5F1E8] rounded-xl font-medium flex items-center gap-2 hover:bg-white/20 transition-colors"
                    >
                      <X className="w-5 h-5" />
                      Reject
                    </button>
                    <button
                      onClick={() => handleAction('alternative')}
                      className="px-6 py-3 bg-white/10 text-[#F5F1E8] rounded-xl font-medium flex items-center gap-2 hover:bg-white/20 transition-colors"
                    >
                      <RefreshCw className="w-5 h-5" />
                      Try Alternative
                    </button>
                    <button
                      onClick={handleDownload}
                      className="px-6 py-3 bg-[#C8A36A] text-black rounded-xl font-medium flex items-center gap-2 hover:bg-[#D4AF37] transition-colors"
                    >
                      <Download className="w-5 h-5" />
                      Download
                    </button>
                  </div>
                  
                  {/* Secondary Actions */}
                  <div className="flex justify-center gap-4 mt-4">
                    <button
                      onClick={() => handleAction('save')}
                      className="px-4 py-2 text-[#F5F1E8]/60 hover:text-[#C8A36A] transition-colors flex items-center gap-2 text-sm"
                    >
                      <Heart className="w-4 h-4" />
                      Save to Project
                    </button>
                    <button
                      onClick={resetFlow}
                      className="px-4 py-2 text-[#F5F1E8]/60 hover:text-[#C8A36A] transition-colors flex items-center gap-2 text-sm"
                    >
                      <Upload className="w-4 h-4" />
                      New Image
                    </button>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        )}
      </main>
    </div>
  );
};

export default SREV2Page;
